import { inject, Injectable, signal } from "@angular/core";
import { Platform } from "@ionic/angular/standalone";

@Injectable({
  providedIn: "root",
})
export class UtilsService {
  platform = inject(Platform);
  isLandscape = signal<boolean>(this.platform.isLandscape());

  constructor() {
    this.updateOrientation();

    window.addEventListener("resize", () => {
      this.updateOrientation();
    });
  }

  /**
   * Function to convert base64 images to File image
   * @param dataurl the base64 image string
   * @param fileType the fileType eg: image/png, image/jpeg, etc...
   * @param filename the name of the file
   * @returns an image of File type.
   */
  async dataURLtoFile(
    dataurl: string,
    fileType: string,
    filename: string
  ): Promise<File> {
    const arr = dataurl.split(",");
    const bstr = atob(arr[1]);
    let n = bstr.length;
    const u8arr = new Uint8Array(n);
    while (n--) {
      u8arr[n] = bstr.charCodeAt(n);
    }
    return new File([u8arr], filename, { type: fileType });
  }

  /**
   * function to get the image from the url and convert it in a base64 image
   * @param imageUrl the image url
   * @returns a promise with the image in base64
   */
  async convertImageUrlToBase64(imageUrl: string): Promise<string> {
    return new Promise((resolve, reject) => {
      // Create an image element
      const img = new Image();

      // Set crossOrigin to anonymous to avoid CORS issues
      img.crossOrigin = "anonymous";

      // When the image is loaded
      img.onload = () => {
        // Create a canvas element
        const canvas = document.createElement("canvas");
        canvas.width = img.width;
        canvas.height = img.height;

        // Draw the image on the canvas
        const ctx = canvas.getContext("2d");
        if (!ctx) {
          reject(new Error("Could not get canvas context"));
          return;
        }

        ctx.drawImage(img, 0, 0);

        // Get the base64 string from the canvas
        const base64String = canvas.toDataURL("image/png").split(",")[1];

        // Resolve with the base64 string
        resolve(base64String);
      };

      // Handle errors
      img.onerror = () => {
        reject(new Error("Failed to load image"));
      };

      // Set the image source to the URL
      img.src = imageUrl;
    });
  }

  /**
   * function to accent-insensitive text filtering, including support for special characters like "ñ"
   * @param text texto to evaluate
   * @returns a text normalized to basic alphabet to filter
   */
  normalizeText(text: string): string {
    return text
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .replace(/ñ/g, "n")
      .replace(/Ñ/g, "n")
      .toLowerCase();
  }

  /**
   * Normalizes different date string representations into a simple ISO format (YYYY-MM-DD).
   *
   * @param dateStr - A date string in different possible formats,
   *                  e.g. "2025-8-17", "Aug 17, 2025", "2025-08-17".
   * @returns A date string in ISO format `YYYY-MM-DD` or `null` if parsing fails.
   */
  normalizeToISO(dateStr?: string | null): string | null {
    // If no value is provided, return null immediately
    if (!dateStr) return null;

    //Case 1: The string is already close to ISO (YYYY-M-D)
    const isoMatch = dateStr.match(/^(\d{4})-(\d{1,2})-(\d{1,2})/);
    if (isoMatch) {
      const y = isoMatch[1]; // year
      const m = String(Number(isoMatch[2])).padStart(2, "0"); // month with 2 digits
      const d = String(Number(isoMatch[3])).padStart(2, "0"); // day with 2 digits
      return `${y}-${m}-${d}`;
    }
    //Case 2: Try parsing with the native Date constructor
    const parsed = new Date(dateStr);
    if (!isNaN(parsed.getTime())) {
      const y = parsed.getFullYear();
      const m = String(parsed.getMonth() + 1).padStart(2, "0"); // getMonth() is zero-based
      const d = String(parsed.getDate()).padStart(2, "0");
      return `${y}-${m}-${d}`;
    }
    //Case 3: Manual parsing for strings like "Aug 17 2025"
    const monthMap: Record<string, number> = {
      jan: 1,
      feb: 2,
      mar: 3,
      apr: 4,
      may: 5,
      jun: 6,
      jul: 7,
      aug: 8,
      sep: 9,
      sept: 9,
      oct: 10,
      nov: 11,
      dec: 12,
    };
    // Clean up commas and extra spaces
    const cleaned = dateStr.replace(",", "").trim();
    // Split into parts, expected something like ["Aug", "17", "2025"]
    const parts = cleaned.split(/\s+/);
    if (parts.length === 3) {
      const monthStr = parts[0].toLowerCase().slice(0, 3); // first 3 letters
      const day = Number(parts[1]);
      const year = Number(parts[2]);
      const mm = monthMap[monthStr];
      // Validate that day, month, and year are valid
      if (!isNaN(day) && !isNaN(year) && mm) {
        return `${year}-${String(mm).padStart(2, "0")}-${String(day).padStart(
          2,
          "0"
        )}`;
      }
    }
    //If no case matched, return null
    return null;
  }

  /**
   * Updates the current device orientation state.
   *
   * This method compares the viewport width and height provided by the Platform service.
   * If the width is greater than the height, it determines that the device is in landscape mode.
   * The result is stored in the `isLandscape` signal for reactive usage across the component.
   */
  private updateOrientation() {
    const width = this.platform.width();
    const height = this.platform.height();

    // Set orientation to landscape if width is larger than height
    this.isLandscape.set(width > height);
  }
}
